import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CONFLICT_DEFAULTS } from '@pirouette/shared';
import { useDancerProductionSchedule, type DancerScheduleRow } from '@/data/conflicts';
import { supabase } from '@/lib/supabase';
import { Empty } from '@/components/ui/Empty';
import { Badge } from '@/components/ui/Badge';

export const Route = createFileRoute('/admin/productions/$id/by-family')({
  component: ByFamily,
});

// Canonical window from the shared module; keeps this rollup aligned with
// detectLineupConflicts.
const SIBLING_ADJACENT_WINDOW = CONFLICT_DEFAULTS.siblingAdjacentWindow;

interface DancerInfo {
  id: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  family_id: string;
}

interface FamilyInfo {
  id: string;
  primary_guardian_name: string;
}

function ByFamily() {
  const { id: productionId } = Route.useParams();
  const schedule = useDancerProductionSchedule(productionId);

  const dancerIds = useMemo(
    () => [...new Set((schedule.data ?? []).map((r) => r.dancer_id))],
    [schedule.data],
  );

  const dancersQuery = useQuery({
    enabled: dancerIds.length > 0,
    queryKey: ['dancers-by-family', [...dancerIds].sort().join(',')],
    queryFn: async (): Promise<DancerInfo[]> => {
      const { data, error } = await supabase
        .from('dancers')
        .select('id, first_name, last_name, preferred_name, family_id')
        .in('id', dancerIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  const familyIds = useMemo(
    () => [...new Set((dancersQuery.data ?? []).map((d) => d.family_id))],
    [dancersQuery.data],
  );

  const familiesQuery = useQuery({
    enabled: familyIds.length > 0,
    queryKey: ['families-by-family-view', [...familyIds].sort().join(',')],
    queryFn: async (): Promise<FamilyInfo[]> => {
      const { data, error } = await supabase
        .from('families')
        .select('id, primary_guardian_name')
        .in('id', familyIds);
      if (error) throw error;
      return data ?? [];
    },
  });

  // Build family → [dancers] map and dancer → [schedule rows] map.
  const dancersByFamily = useMemo(() => {
    const m = new Map<string, DancerInfo[]>();
    for (const d of dancersQuery.data ?? []) {
      const list = m.get(d.family_id) ?? [];
      list.push(d);
      m.set(d.family_id, list);
    }
    return m;
  }, [dancersQuery.data]);

  const scheduleByDancer = useMemo(() => {
    const m = new Map<string, DancerScheduleRow[]>();
    for (const r of schedule.data ?? []) {
      const list = m.get(r.dancer_id) ?? [];
      list.push(r);
      m.set(r.dancer_id, list);
    }
    return m;
  }, [schedule.data]);

  // Family → list of sibling adjacency warnings within this production.
  const siblingWarnings = useMemo(() => {
    const warnings = new Map<
      string,
      { show_name: string; distance: number; a: string; b: string }[]
    >();
    for (const family of familiesQuery.data ?? []) {
      const fDancers = dancersByFamily.get(family.id) ?? [];
      if (fDancers.length < 2) continue;
      // Build per-show, per-family appearance map.
      const byShow = new Map<
        string,
        { dancer_id: string; sequence_index: number; show_name: string }[]
      >();
      for (const dancer of fDancers) {
        const apps = scheduleByDancer.get(dancer.id) ?? [];
        for (const app of apps) {
          const list = byShow.get(app.show_id) ?? [];
          list.push({
            dancer_id: dancer.id,
            sequence_index: app.sequence_index,
            show_name: app.show_name,
          });
          byShow.set(app.show_id, list);
        }
      }
      const list: { show_name: string; distance: number; a: string; b: string }[] = [];
      for (const [, appearances] of byShow) {
        const sorted = [...appearances].sort((x, y) => x.sequence_index - y.sequence_index);
        for (let i = 0; i < sorted.length; i++) {
          for (
            let j = i + 1;
            j < sorted.length &&
            sorted[j]!.sequence_index - sorted[i]!.sequence_index <= SIBLING_ADJACENT_WINDOW;
            j++
          ) {
            if (sorted[i]!.dancer_id === sorted[j]!.dancer_id) continue;
            list.push({
              show_name: sorted[i]!.show_name,
              distance: sorted[j]!.sequence_index - sorted[i]!.sequence_index,
              a: sorted[i]!.dancer_id,
              b: sorted[j]!.dancer_id,
            });
          }
        }
      }
      if (list.length > 0) warnings.set(family.id, list);
    }
    return warnings;
  }, [familiesQuery.data, dancersByFamily, scheduleByDancer]);

  const familyById = useMemo(
    () => new Map((familiesQuery.data ?? []).map((f) => [f.id, f])),
    [familiesQuery.data],
  );

  const dancerNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of dancersQuery.data ?? []) {
      m.set(d.id, `${d.preferred_name ?? d.first_name} ${d.last_name}`);
    }
    return m;
  }, [dancersQuery.data]);

  const sortedFamilyIds = useMemo(
    () =>
      [...dancersByFamily.keys()].sort((a, b) => {
        const fa = familyById.get(a)?.primary_guardian_name ?? '';
        const fb = familyById.get(b)?.primary_guardian_name ?? '';
        return fa.localeCompare(fb);
      }),
    [dancersByFamily, familyById],
  );

  return (
    <div className="space-y-8">
      <header>
        <Link
          to="/admin/productions/$id"
          params={{ id: productionId }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Production
        </Link>
        <h1 className="mt-2">By family</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Families with dancers in this production, with sibling-adjacency warnings flagged so you
          can stage parent quick-changes backstage.
        </p>
      </header>

      {schedule.isLoading && <p aria-live="polite">Loading…</p>}

      {sortedFamilyIds.length === 0 && !schedule.isLoading && (
        <Empty
          title="No families with cast yet"
          description="Cast assignments will populate this view once dancers are placed in dances."
        />
      )}

      {sortedFamilyIds.length > 0 && (
        <ul className="grid gap-4">
          {sortedFamilyIds.map((familyId) => {
            const family = familyById.get(familyId);
            const fDancers = dancersByFamily.get(familyId) ?? [];
            const warns = siblingWarnings.get(familyId) ?? [];
            return (
              <li key={familyId} className="surface-elevated p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-[var(--font-display)] text-[var(--text-xl)]">
                    {family?.primary_guardian_name ?? 'Family'}
                  </h2>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {fDancers.length} {fDancers.length === 1 ? 'dancer' : 'siblings'}
                  </p>
                </div>

                <ul className="mt-3 flex flex-wrap gap-2">
                  {fDancers.map((d) => (
                    <li key={d.id}>
                      <Badge tone="accent">
                        {d.preferred_name ?? d.first_name} {d.last_name}
                      </Badge>
                    </li>
                  ))}
                </ul>

                {warns.length > 0 && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-[var(--color-warn)]">
                      ⚠ {warns.length} sibling-adjacency{' '}
                      {warns.length === 1 ? 'warning' : 'warnings'}
                    </summary>
                    <ul className="mt-2 space-y-1 text-[var(--text-sm)]">
                      {warns.map((w, i) => (
                        <li key={i} className="text-[var(--color-ink-muted)]">
                          <span className="font-medium">{dancerNameById.get(w.a)}</span> and{' '}
                          <span className="font-medium">{dancerNameById.get(w.b)}</span>{' '}
                          {w.distance === 1
                            ? 'in back-to-back dances'
                            : `within ${w.distance} dances`}{' '}
                          on <em className="not-italic">{w.show_name}</em>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
